/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import {
    now,
    getLocalTimeZone,
    CalendarDateTime,
    parseDate,
    parseAbsolute,
    toCalendarDateTime,
    toZoned,
    ZonedDateTime,
    CalendarDate,
    Time,
} from '@internationalized/date';
import extend from 'lodash/extend';

import {
    formatRegex,
    gnoment as _gnoment,
    Gnoment as BaseGnoment,
} from 'shared-ui/gnoment';

import {
    getDateOnYearWithDayOffset,
    getDaysSinceBeginningOfYear,
    isJustTimeStr,
    padLeftIfNeeded,
    parseDateTimeWithAbsoluteAndZoned,
    parseJustTimeStr,
    stripDayOfWeekPrefix,
} from './gnoment-ftm';

export {
    calendarDateToGnoment,
    gnomentRangeToRangeValue,
    gnomentToCalendarDate,
    gnomentToCalendarDateTime,
    rangeValueToGnomentRange,
} from 'shared-ui/gnoment';

let defaultTimeZone = getLocalTimeZone();

const parseFlexibleDate = (input, timeZone = 'UTC') => {
    let res;
    //Matches MM/DD/YYYY
    // ftm customization (handling M/DD/YYYY and MM/D/YYYY too)
    // const dateMMDDYYYYRegex = /^\d{2}\/\d{2}\/\d{4}$/;
    const dateMMDDYYYYRegex = /^\d{1,2}\/\d{1,2}\/\d{4}$/;

    // ftm customization
    input = stripDayOfWeekPrefix(input);

    //Matches written out date: January 1 or January 1, 2025
    const writtenoutDateRegex = /^[A-Za-z]+ \d{1,2}(?:, \d{4})?$/;
    if (input.match(writtenoutDateRegex)) {
        let formattedInput = input.trim();
        if (!formattedInput.includes(',')) {
            const currentYear =
                timeZone === 'UTC'
                    ? new Date().getUTCFullYear()
                    : new Date().getFullYear();

            formattedInput = `${formattedInput}, ${currentYear}`;
        }
        const tempDate = new Date(formattedInput);
        const isoDate = tempDate.toISOString().slice(0, 10);

        let d = parseDate(isoDate);
        d = toZoned(d, timeZone);
        d = d.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
        res = d;
    } else if (input.length === 8 || input.length === 9 || input.length === 10) {
        // ftm customization (handling M/DD/YYYY and MM/D/YYYY too)
        // If the input is exactly "YYYY-MM-DD", assume it's a date without time
        if (input.includes('-')) {
            let d = parseDate(input);
            d = toZoned(d, timeZone);
            d = d.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
            res = d;
        }

        //If matches the regex format and includes slashes (/) assume it's MM/DD/YYYY
        else if (input.includes('/') && input.match(dateMMDDYYYYRegex)) {
            const date = input.split('/');
            const month = padLeftIfNeeded(date[0], '0', 2); // ftm customization
            const day = padLeftIfNeeded(date[1], '0', 2); // ftm customization
            const year = date[2];

            const isoFormatted = `${year}-${month}-${day}`;
            let d = parseDate(isoFormatted);
            d = toZoned(d, timeZone);
            d = d.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
            res = d;
        }
    } else if (input.includes('Z')) {
        // If it has a 'Z' (UTC) or timezone offset, treat as absolute timestamp
        res = parseAbsolute(input, timeZone);
    } else {
        // Otherwise, assume it's a local date-time string
        //Handle hunting permit weird multi-dates & content mgmt
        if (input.includes('/') || input.includes(';') || input.includes('of')) {
            return null;
        }

        let inputUpdated = input;
        if (input.length > 11 && input[10] === ' ') {
            inputUpdated = `${input.substring(0, 10)}T${input.substring(11)}`;
        }

        // ftm customization
        // const d = parseDateTime(inputUpdated);
        if (isJustTimeStr(inputUpdated)) {
            res = parseJustTimeStr(inputUpdated);
        } else {
            const d = parseDateTimeWithAbsoluteAndZoned(inputUpdated);

            res = toZoned(d, timeZone);
        }
    }
    return res;
};

export class Gnoment extends BaseGnoment {
    constructor(date) {
        super();

        this.zonedDateTime = null;
        if (date === undefined || date === '') {
            this.zonedDateTime = now(defaultTimeZone);
        } else if (typeof date === 'string' && date !== '') {
            // ftm customization
            if (isJustTimeStr(date)) {
                this.zonedDateTime = parseJustTimeStr(date);
            } else {
                const dateObj = parseFlexibleDate(date, defaultTimeZone);
                if (dateObj) {
                    this.zonedDateTime = toCalendarDateTime(dateObj);
                    this.zonedDateTime = toZoned(this.zonedDateTime, defaultTimeZone);
                }
            }
        } else if (date instanceof Time) {
            // just time
            this.zonedDateTime = date;
        } else if (date instanceof Date) {
            if (!Number.isNaN(date.getFullYear())) {
                const dateTime = new CalendarDateTime(
                    date.getFullYear(),
                    date.getMonth() + 1,
                    date.getDate(),
                    date.getHours(),
                    date.getMinutes(),
                    date.getSeconds()
                );
                this.zonedDateTime = toZoned(dateTime, defaultTimeZone);
            }
        } else if (date instanceof ZonedDateTime) {
            this.zonedDateTime = date;
        } else if (date instanceof CalendarDate) {
            this.zonedDateTime = toZoned(date, defaultTimeZone);
        } else if (date instanceof BaseGnoment) {
            this.zonedDateTime = date.zonedDateTime;
        }
        this.inUTCMode = false;
    }

    dayOfYear(...params) {
        // ftm customization
        if (params.length === 0) {
            return getDaysSinceBeginningOfYear(this);
        }
        return getDateOnYearWithDayOffset(this, params[0]);
    }

    add = (duration, unit) => {
        if (!this.isValid()) return new Gnoment(null);
        const unitTemp = this.unitLookup(unit);
        if (this.zonedDateTime instanceof Time && typeof duration === 'string') {
            this.zonedDateTime = this.zonedDateTime.add({ [unitTemp]: Number(duration) });
        } else {
            this.zonedDateTime = this.zonedDateTime.add({ [unitTemp]: duration });
        }
        return new Gnoment(this.zonedDateTime);
    };

    format = (format, timezone = null) => {
        if (!this.isValid()) return 'Invalid date';

        // Create date object and handle timezone if provided
        let tzUpdated = timezone;
        let formatUpdated = format;

        if (!formatUpdated) {
            formatUpdated = this.inUTCMode
                ? 'YYYY-MM-DDTHH:mm:ss[Z]'
                : 'YYYY-MM-DDTHH:mm:ssZ';
        }

        if (!tzUpdated) tzUpdated = this.zonedDateTime.timeZone;

        let date, d;
        if (this.zonedDateTime instanceof Time) {
            // just time
            date = new Date(
                0,
                0,
                0,
                this.zonedDateTime.hour,
                this.zonedDateTime.minute,
                this.zonedDateTime.second,
                this.zonedDateTime.millisecond
            );
            d = date;
        } else {
            date = this.zonedDateTime.toDate(tzUpdated);
            d = new Date(date);
        }
        let timezoneShort = '';

        const getDateEnding = (day) => {
            switch (day % 10) {
                case 1:
                    return 'st';
                case 2:
                    return 'nd';
                case 3:
                    return 'rd';
                default:
                    return 'th';
            }
        };

        // Helper function to get timezone offset in format ±HH:mm
        const getTimezoneOffset = () => {
            return this.convertOffsetToHoursMinutes(this.zonedDateTime.offset);
        };

        const getCachedFormatter = (options) => {
            const key = JSON.stringify({
                locale: 'en-US',
                timeZone: tzUpdated,
                ...options,
            });

            if (!Gnoment.formatterCache[key]) {
                Gnoment.formatterCache[key] = new Intl.DateTimeFormat('en-US', {
                    ...options,
                    timeZone: tzUpdated,
                });
            }
            return Gnoment.formatterCache[key];
        };

        const formatDate = (options) => getCachedFormatter(options).format(d);
        // Extract the part corresponding to the timezone name

        const getShortTZ = () => {
            const parts = new Intl.DateTimeFormat('en-US', {
                timeZone: tzUpdated,
                timeZoneName: 'short',
            }).formatToParts(date);
            const tzNamePart = parts.find((part) => part.type === 'timeZoneName');
            timezoneShort = tzNamePart ? tzNamePart.value : '';
            return timezoneShort;
        };

        const YYYYFormat = formatDate({
            year: 'numeric',
        });

        const formatTokens = {
            // Year
            YYYY: () =>
                YYYYFormat.length === 1 ? YYYYFormat.padStart(4, '0') : YYYYFormat,
            YY: () => formatDate({ year: '2-digit' }),

            // Month
            MM: () => formatDate({ month: '2-digit' }),
            M: () => formatDate({ month: 'numeric' }),
            MMMM: () => formatDate({ month: 'long' }),
            MMM: () => formatDate({ month: 'short' }),

            // Day
            DD: () => formatDate({ day: '2-digit' }),
            Do: () => formatDate({ day: 'numeric' }).concat(getDateEnding(d.getDate())),
            D: () => formatDate({ day: 'numeric' }),

            dddd: () => formatDate({ weekday: 'long' }),
            ddd: () => formatDate({ weekday: 'short' }),

            // Hour
            HH: () =>
                formatDate({
                    hour: '2-digit',
                    hour12: false,
                }).replace(/24/g, '00'),
            H: () =>
                formatDate({
                    hour: 'numeric',
                    hour12: false,
                }).replace(/24/g, '0'),
            hh: () =>
                formatDate({
                    hour: '2-digit',
                    hour12: true,
                }).replace(/ AM| PM/g, ''),
            h: () =>
                formatDate({
                    hour: 'numeric',
                    hour12: true,
                }).replace(/ AM| PM/g, ''),

            // Minute
            mm: () => formatDate({ minute: '2-digit' }).padStart(2, '0'),
            m: () => formatDate({ minute: 'numeric' }),

            ss: () => formatDate({ second: '2-digit' }).padStart(2, '0'),
            s: () => formatDate({ second: 'numeric' }),

            // Millisecond
            sss: () =>
                formatDate({
                    millisecond: 'short',
                }),
            SSS: () =>
                formatDate({
                    fractionalSecondDigits: 3,
                }).split('.')[1] || '000',
            // AM/PM
            A: () =>
                formatDate({
                    hour12: true,
                    hour: 'numeric',
                }).split(' ')[1],
            a: () =>
                formatDate({
                    hour12: true,
                    hour: 'numeric',
                })
                    .split(' ')[1]
                    .toLowerCase(),

            // Timezone
            ZZ: () => getTimezoneOffset(d, tzUpdated).replace(':', ''),
            Z: () => getTimezoneOffset(d, tzUpdated),
            z: () => getShortTZ(d, tzUpdated) || defaultTimeZone,
        };

        // Replace tokens with actual values
        if (formatUpdated.includes('LT')) {
            formatUpdated = formatUpdated.replace('LT', 'h:mm A');
        }
        if (formatUpdated.includes('LTS')) {
            formatUpdated = formatUpdated.replace('LTS', 'h:mm:ss A');
        }
        if (formatUpdated.includes('LL')) {
            formatUpdated = formatUpdated.replace('LL', 'MMMM D, YYYY');
        }
        if (formatUpdated.includes('ll')) {
            formatUpdated = formatUpdated.replace('ll', 'MMM D, YYYY');
        }
        if (formatUpdated.includes('l')) {
            formatUpdated = formatUpdated.replace('l', 'M/D/YYYY');
        }

        if (formatUpdated.includes('x')) {
            formatUpdated = formatUpdated.replace('x', String(gnoment(d).unix() * 1000));
        }

        return formatUpdated.replace(
            formatRegex,
            (match, contents) => contents || formatTokens[match]()
        );
    };
}

function gnoment(d = undefined) {
    return new Gnoment(d);
}

/**
 * Using any additional `gnoment` methods will need to be added to the extension here since the wrapper
 * needs to return an instance of the extended `Gnoment` class. Methods needs to reference the `gnoment`
 * declared here.
 */
extend(gnoment, {
    ..._gnoment,

    tz: (...params) => {
        let zoneDateTime;

        if (params.length === 2) {
            if (params[0] === undefined) {
                zoneDateTime = gnoment(undefined).tz(params[1]);
            } else if (params[0] === null) {
                zoneDateTime = gnoment(null).tz(params[1]);
            } else {
                zoneDateTime = parseFlexibleDate(params[0], params[1]);
            }

            return gnoment(zoneDateTime);
        }

        if (params.length === 1) {
            return gnoment().tz(params[0]);
        }
        return gnoment(null);
    },

    parseZone: (input) => {
        let zonedDateTime;
        const zoneOffset = /.*([+-]\d{2}:?\d{2})/;
        const found = input.match(zoneOffset);
        if (input.includes('Z')) {
            zonedDateTime = parseAbsolute(input, 'UTC');
        } else if (found) {
            zonedDateTime = parseAbsolute(input, found[1]);
        } else {
            //Todo: add test case if you run into this
            return gnoment(input);
        }

        return gnoment(zonedDateTime);
    },

    utc: (date) => gnoment(date).utc(),
    invalid: () => gnoment(null),
});

gnoment.tz.setDefault = (tz) => {
    defaultTimeZone = tz;
};

export { gnoment };
export default gnoment;
